import { useSyncExternalStore } from 'react';

import {
  parseDisabledConnectors,
  withConnectorEnabled,
  type SourceConnectorId,
} from '@/components/chat/SourceConnectors';

export { disabledConnectorsForRequest } from '@/components/chat/SourceConnectors';

/**
 * Which optional source connectors the voter has switched off.
 *
 * Stored in this browser as a JSON array of connector ids, under the same key
 * and shape as the mobile app (`services/sourceConnectorPrefs.ts`). Every
 * optional connector starts on: an empty or missing value means nothing is off,
 * and the chat request then carries no `disabled_connectors` at all — which is
 * also what a partner's cached embed sends.
 */
export const STORAGE_KEY = 'vm_disabled_connectors';

function readStored(): SourceConnectorId[] {
  if (typeof window === 'undefined') return [];
  try {
    return parseDisabledConnectors(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return [];
  }
}

// Module-level store so the composer, the Settings card, and the request
// builder read one value (the pattern `useNavMode` uses).
let current: SourceConnectorId[] = readStored();
const listeners = new Set<() => void>();
const EMPTY: SourceConnectorId[] = [];

function commit(next: SourceConnectorId[]): void {
  current = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Synchronous read, for the chat request builder. */
export function getDisabledConnectors(): SourceConnectorId[] {
  return current;
}

export function setConnectorEnabled(id: SourceConnectorId, enabled: boolean): void {
  const next = withConnectorEnabled(current, id, enabled);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Private mode / storage disabled — the in-memory choice still applies.
  }
  commit(next);
}

// Cross-tab sync: a switch flipped in one tab applies to the next send in another.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) commit(parseDisabledConnectors(event.newValue));
  });
}

/** Reactive read of the switched-off connectors, and the setter. */
export default function useSourceConnectorPrefs() {
  const disabled = useSyncExternalStore(subscribe, getDisabledConnectors, () => EMPTY);
  return { disabled, setEnabled: setConnectorEnabled };
}
